import React, {
  useCallback,
  useEffect,
  useState,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
} from "react";

import { CircularProgress, Grid } from "@mui/material";
import { Stack } from "@mui/material";
import { format, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { FiUpload } from "react-icons/fi";
import { VscFilePdf } from "react-icons/vsc";
import { Item } from "semantic-ui-react";
import * as yup from "yup";

import useYupValidation from "../../../hooks/useYupValidation";
import { Button, Input } from "../../Form";
import Modal from "../../Generic/Modal";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import useStyles from "./styles";

interface UploadDocumentProps {
  isLoading: boolean;
  onUploadDocument?: (
    url: string,
    name: string,
    dueDate: Date,
    callbackFunction?: () => void
  ) => void;
  loadingUpload?: boolean;
  isExtraFieldFill?: boolean;
  renderAutosuggestInput?: React.ReactChild;
}

interface UploadFile {
  name: string;
  dueDate: string;
  file?: File;
}

export interface UploadDocumentHandler {
  onCloseModal: () => void;
  onOpenModal: () => void;
}

const FILE_MIX_SIZE = 2 * 1024 * 1024;

const DEFAULT_FIELDS_VALUES: UploadFile = { name: "", dueDate: "" };

const UploadDocumentModal: ForwardRefRenderFunction<UploadDocumentHandler, UploadDocumentProps> = (
  props,
  ref
) => {
  const { isLoading, loadingUpload, renderAutosuggestInput, isExtraFieldFill, onUploadDocument } =
    props;

  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [fields, setFields] = useState<UploadFile>(DEFAULT_FIELDS_VALUES);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const [, setTryAgainButtonIsDisable] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const hasAutossugest: boolean = !!renderAutosuggestInput;

  const onCloseThisModal = useCallback(() => {
    setOpenModal(false);
    setFields(DEFAULT_FIELDS_VALUES);
    setErrors([]);
  }, []);

  const schema = yup.object().shape({
    name: yup.string().required(translate("thisFieldIsRequired")),
    dueDate: yup.string().required(translate("thisFieldIsRequired")),
  });

  const debounce = useCallback(() => {
    if (!loadingUpload) return setTimeout(() => {});

    return setTimeout(() => {
      setTryAgainButtonIsDisable(false);
    }, 5000);
  }, [loadingUpload]);

  useEffect(() => {
    debounce();

    return () => clearTimeout(debounce());
  }, [loadingUpload, debounce]);

  const validation = () => {
    try {
      schema.validateSync(
        {
          name: fields?.name,
          dueDate: fields?.dueDate,
        },
        { abortEarly: false }
      );

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  const onDropFile = (files: File[]) => {
    if (!files || !files.length) return;

    const file = files[0];

    const splittedFileName = file.name.split(".");
    const extensionFromName = splittedFileName[splittedFileName.length - 1];

    if (file?.size && file?.size > FILE_MIX_SIZE) {
      NotificationToast.error(translate("theFileMustBeLessThan2mbInSize"));

      return;
    }

    if (file && extensionFromName === "pdf") {
      setFields((prev) => ({ ...prev, file }));
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: onDropFile,
    maxFiles: 1,
    accept: ["application/pdf"],
  });

  const onConfirmUpload = () => {
    if (!validation()) return;
    if (!fields.file) return;

    const reader = new FileReader();

    reader.addEventListener("load", () => {
      const uri = reader.result?.toString();

      if (uri && onUploadDocument) {
        onUploadDocument(uri, fields.name, new Date(fields.dueDate), onCloseThisModal);
      }
    });
    reader.readAsDataURL(fields.file);
  };

  const renderDropzone = () => (
    <div {...getRootProps()} className={classes.dropzone}>
      <input {...getInputProps()} />
      <Stack justifyContent={"center"} alignItems={"center"} className={classes.button}>
        <Item className={classes.textButton}>
          <FiUpload size={24} />
        </Item>
        <Item className={classes.textButton}>{translate("dragOrClickToUploadYourFile")}</Item>
        <Item className={classes.textButtonInfo}>{`${translate("maxSize")} ${translate(
          "2Mb"
        )}`}</Item>
        <Item className={classes.textButtonInfo}>
          {translate("formats")}:{" "}
          <span className={classes.textButtonFormat}>{translate("pdf")}</span>
        </Item>
      </Stack>
    </div>
  );

  const showFile = () => {
    return (
      <div className={classes.showFile}>
        <div className={classes.showFileLeft}>
          <VscFilePdf size={16} className={classes.redColor} />
          <span className={classes.filename}>{fields?.file?.name}</span>
        </div>

        <Button
          ghost={true}
          startIcon={<AiOutlineCloseCircle size={20} className={classes.redColor} />}
          onClick={() => setFields((prev) => ({ ...prev, file: undefined }))}
        ></Button>
      </div>
    );
  };

  const renderInputs = () => {
    return (
      <Grid container spacing={4} direction={"row"}>
        <Grid item xs={12} sm={hasAutossugest ? 4 : 6}>
          <Input
            data-cy="name-input"
            disabled={isLoading}
            name="name"
            label={translate("name")}
            placeholder={"Ex: Admin"}
            error={fieldHasError("name")}
            errorMessage={getErrorByField("name")}
            value={fields.name}
            onChange={(event) => setFields((prev) => ({ ...prev, name: event.target.value }))}
            required
          />
        </Grid>
        <Grid item xs={12} sm={hasAutossugest ? 4 : 6}>
          <Input
            disabled={isLoading}
            label={translate("dueDate")}
            type="date"
            name="dueDate"
            disablePast={true}
            error={fieldHasError("dueDate")}
            errorMessage={getErrorByField("dueDate")}
            required
            value={
              fields.dueDate
                ? format(
                    utcToZonedTime(
                      fields.dueDate,
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    ),
                    "yyyy-MM-dd"
                  )
                : ""
            }
            onChange={(event) => {
              setFields((prev) => ({
                ...prev,
                dueDate: zonedTimeToUtc(
                  event.target.value,
                  Intl.DateTimeFormat().resolvedOptions().timeZone
                ).toISOString(),
              }));
            }}
            data-cy="input-date"
          />
        </Grid>
        {renderAutosuggestInput && (
          <Grid item xs={12} sm={4}>
            {renderAutosuggestInput}
          </Grid>
        )}
        <Grid item sm={12}>
          {!fields?.file ? renderDropzone() : showFile()}
        </Grid>
      </Grid>
    );
  };

  const renderContentLoading = () => {
    return (
      <div className={classes.loadingMainDiv}>
        <div className={classes.loadingContainer}>
          <span className={classes.loadingTitle}>
            {translate("weAreLoadingTheUploadedContentPleaseWait")}
          </span>

          <CircularProgress className={classes.loadingProgress} />
          <div></div>
          {/* TODO disable for now, need avaliate this feature */}
          {/* <span className={classes.loadingInformation}>
            {translate("ifThisIsTakingTooLong")}
            <Button
              className={classes.tryAgainButton}
              onClick={() => {
                setTryAgainButtonIsDisable(true);
                onConfirmUpload();
                debounce();
              }}
              disabled={tryAgainButtonIsDisable}
            >
              {translate("tryAgain")}
            </Button>
          </span> */}
        </div>
      </div>
    );
  };

  useImperativeHandle(ref, () => ({
    onCloseModal: onCloseThisModal,
    onOpenModal: () => setOpenModal(true),
  }));

  return (
    <Modal
      className={!loadingUpload ? classes.uploadModal : classes.loadingModal}
      open={openModal}
      showActionButtons={!loadingUpload}
      actionButtonProps={{
        onCancel: onCloseThisModal,
        onSave: onConfirmUpload,
        textSave: translate("upload"),
        loading: loadingUpload,
        cancelDisabled: loadingUpload,
        saveDisabled: !fields.name || !fields.dueDate || !fields.file || !isExtraFieldFill,
      }}
      title={!loadingUpload ? translate("uploadFile") : ""}
      handleClose={!loadingUpload ? onCloseThisModal : undefined}
      showModalFooter={!loadingUpload}
      data-cy="upload-new-file-modal"
    >
      {!loadingUpload ? renderInputs() : renderContentLoading()}
    </Modal>
  );
};

export default forwardRef(UploadDocumentModal);
